import React from "react";
import styled from "styled-components";
import { defineMessages, useIntl } from "react-intl";
import GatsbyImage, { FluidObject, GatsbyImageProps } from "gatsby-image";
import { WideContainer } from "../../../shared/components/WideContainer";
import ReadyToHireHeader from "./ReadyToHireHeader";

export interface ReadyToHireProps {
  callToActionUrl: string;
  displayCta: string;
  womanCookingWithDaughterFluid: FluidObject;
}

const messages = defineMessages({
  readyToHireImageAltText: {
    id: "TaskTemplateServiceBreadcrumbs.readyToHireImageAltText",
    defaultMessage: "A woman cooking with her daughter.",
  },
});

export function ReadyToHire({
  callToActionUrl,
  displayCta,
  womanCookingWithDaughterFluid,
}: ReadyToHireProps) {
  const onClick = (event: React.MouseEvent) => {
    event.preventDefault();
    window.location.href = callToActionUrl;
  };

  const intl = useIntl();

  if (displayCta && callToActionUrl) {
    return (
      <BlueBackgroundSection>
        <WideContainer>
          <ReadyToHireContainer>
            <ImageContainer>
              <StyledGatsbyImage
                fluid={womanCookingWithDaughterFluid}
                alt={intl.formatMessage(messages.readyToHireImageAltText)}
              />
            </ImageContainer>
            <ReadyToHireSpacer />
            <ReadyToHireTextContainer>
              <ReadyToHireHeader />
              <div>
                <StyledButton onClick={onClick}>{displayCta}</StyledButton>
              </div>
            </ReadyToHireTextContainer>
          </ReadyToHireContainer>
        </WideContainer>
      </BlueBackgroundSection>
    );
  }

  return null;
}

const StyledButton = styled.a`
  width: 250px;
  padding: 0.5625em 1.125em 0.375em 1.125em;
  color: ${(p) => p.theme.color.buttonTextPrimary};
  border: 1px solid ${(p) => p.theme.color.buttonBorderPrimary};
  background-color: ${(p) => p.theme.color.buttonBackgroundPrimary};
  appearance: none;
  display: inline-block;
  text-align: center;
  border-radius: ${(p) => p.theme.layout.borderRadius.btn};
  font-size: ${(p) => p.theme.typography.typeScale[100]};
  font-weight: ${(p) => p.theme.typography.weight.semiBold};
  outline: none;
  cursor: pointer;
  transition-property: background, color, border-color, box-shadow;
  transition-timing-function: ease;
  transition-duration: 0.16s;
  line-height: 1.4;
  user-select: none;
  text-decoration: none;

  &:hover {
    color: ${(p) => p.theme.color.buttonTextPrimaryHover};
    border: 1px solid ${(p) => p.theme.color.buttonBorderPrimaryHover};
    background-color: ${(p) => p.theme.color.buttonBackgroundPrimaryHover};
  }
`;

const BlueBackgroundSection = styled.section`
  background: #e5f3f5;
`;
const StyledGatsbyImage = styled(GatsbyImage)<GatsbyImageProps>`
  display: block;
  width: 100%;
  overflow: hidden;

  @media (max-width: 800px) {
    object-fit: cover;
    max-height: 190px;
  }
`;

const ReadyToHireContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 2.5rem;
  padding-bottom: 2.5rem;

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ReadyToHireTextContainer = styled.div`
  align-items: start;
  display: flex;
  flex-basis: 35%;
  flex-direction: column;
  flex-grow: 0;
  flex-shrink: 1;
  justify-content: center;
`;

const ReadyToHireSpacer = styled.div`
  flex-grow: 0;
  flex-shrink: 1;
  flex-basis: 5%;
`;

const ImageContainer = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  width: 100%;
  padding: 0 7px;

  ${(props) => props.theme.breakpoints.desktopUp} {
    width: 60%;
    padding: 0;
  }
`;
